import React from 'react';
import { Box, Flex, FormLabel, Input, InputGroup, InputLeftElement, Image, InputProps } from '@chakra-ui/react';

import { UndoEdit } from '../../iconButtons/UndoEdit';
import { CustomFormControl, DirectionProp } from '../CustomFormControl';

import flags from '../../../assets/flags/flags.png';
import { BLANK_PIXEL_DATA_URL as blank } from '../../../assets/flags/blankPixel';
import '../../../assets/flags/flags.scss';
import { countryPhoneCodes, countryNames } from 'platform-data';
const MAX_COUNTRY_CODE_LENGTH = 3;

export interface IValidationError {
  isCodeValid: boolean;
  isNumberValid: boolean;
  codeValidationErrorMessage: string;
  numberValidationErrorMessage: string;
}

const getCountryCode = (code: string): string => {
  if (!code) return '';
  for (const [countryCode, phoneCode] of Object.entries(countryPhoneCodes)) {
    if (phoneCode === code) return countryCode;
  }
  return '';
};

const getCountryName = (countryCode: string): string => {
  if (!countryCode) return '';
  return countryNames[countryCode as keyof typeof countryNames] || '';
};

export interface IPhoneNumberInputV2Props extends DirectionProp {
  id: string;
  codeValue?: string;
  numberValue?: string;
  label?: string;
  phoneCodeName?: string;
  phoneNumberName?: string;
  subText?: string;
  countryCodeLabel?: string;
  phoneLabel?: string;
  errorTextCode?: string;
  errorTextNumber?: string;
  forwardedRef?: React.Ref<HTMLInputElement>;
  onChangeCode?: (event: React.FormEvent<HTMLInputElement>) => void;
  onChangeNumber?: (event: React.FormEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FormEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FormEvent<HTMLInputElement>) => void;
  onValidationFailed?: (error: IValidationError) => void;
  onValidationSuccess?: () => void;
  canUndo?: boolean;
  onUndo?: () => void;
  size?: 'sm' | 'md' | 'lg';
  mb?: number;
  isDisabled?: boolean;
  inputProps?: InputProps & { 'data-sentry-mask'?: string };
}

export const PhoneNumberInputV2: React.FC<IPhoneNumberInputV2Props> = props => {
  const {
    id,
    codeValue = '',
    numberValue = '',
    label,
    phoneCodeName,
    phoneNumberName,
    subText,
    countryCodeLabel = 'Country code',
    phoneLabel = 'Phone number',
    errorTextCode,
    errorTextNumber,
    forwardedRef,
    onChangeCode,
    onChangeNumber,
    onBlur,
    onFocus,

    canUndo = false,
    onUndo,
    size = 'lg',
    mb = 0,
    direction,
    isDisabled = false,
    inputProps,
  } = props;

  const countryCode = getCountryCode(codeValue);
  const countryName = getCountryName(countryCode);

  const handleCodeChange = (event: React.FormEvent<HTMLInputElement>): void => {
    onChangeCode?.(event);
  };

  const handleNumberChange = (event: React.FormEvent<HTMLInputElement>): void => {
    onChangeNumber?.(event);
  };

  const handleFocus = (event: React.FormEvent<HTMLInputElement>): void => {
    onFocus?.(event);
  };

  // Only mark invalid if external (Zod/RHF) error text is provided - ignore internal validation
  const isCodeInputInvalid = !!errorTextCode;
  const isNumberInputInvalid = !!errorTextNumber;
  // Get error message for CustomFormControl
  const errorMessage = errorTextCode || errorTextNumber;

  return (
    <CustomFormControl
      id={id}
      error={errorMessage}
      controlProps={{
        isInvalid: !!errorMessage,
        mb,
      }}
      size={size}
      label={label}
      labelProps={{
        htmlFor: `${id}-code-input-field`,
      }}
      subLabel={subText}
      forwardedRef={forwardedRef}
      direction={direction}
    >
      <Flex flexDirection={{ base: 'column', lg: 'row' }} gap={{ base: 4, lg: 6 }} mb={2}>
        <Flex flexDirection="column" flexGrow={{ base: 0, lg: 0 }} width={{ base: '100%', lg: '33%' }} gap={1}>
          <InputGroup size={size}>
            <InputLeftElement pointerEvents="none" width="auto" px={{ base: 3, lg: 4 }} color="muted">
              {countryCode ? (
                <Image
                  data-selector={`${id}-flag`}
                  src={blank}
                  className={`flag flag-${countryCode.toLowerCase()}`}
                  alt={countryName}
                  title={countryName}
                  bgImage={`url(${flags})`}
                  bgRepeat="no-repeat"
                  width="16px"
                  height="11px"
                  display={{ base: 'none', lg: 'block' }}
                />
              ) : (
                <Box
                  data-selector={`${id}-flag-placeholder`}
                  bgColor="chakra-subtle-bg"
                  borderWidth={1}
                  width="16px"
                  height="11px"
                  display={{ base: 'none', lg: 'block' }}
                />
              )}

              <Box ml={{ base: 0, lg: 2 }}>+</Box>
            </InputLeftElement>

            <Input
              data-selector={`${id}-code-input-field`}
              id={`${id}-code-input-field`}
              name={phoneCodeName}
              onChange={handleCodeChange}
              type="text"
              inputMode="numeric"
              maxLength={MAX_COUNTRY_CODE_LENGTH}
              autoComplete="off"
              value={codeValue}
              isInvalid={isCodeInputInvalid}
              onBlur={onBlur}
              onFocus={handleFocus}
              pl={{ base: 6, lg: 14 }}
              pr={{ base: 1, lg: 4 }}
              disabled={isDisabled}
              size={size}
              {...inputProps}
            />
          </InputGroup>

          <FormLabel
            data-selector={`${id}-country-code-label`}
            htmlFor={`${id}-code-input-field`}
            id={`${id}-code-input-label`}
            fontSize="sm"
            fontWeight="medium"
          >
            {countryCodeLabel}
          </FormLabel>
        </Flex>

        <Flex flexDirection="column" flexGrow="1" width={{ base: '100%', lg: 'auto' }} gap={1}>
          <InputGroup size={size}>
            <InputLeftElement pointerEvents="none" width="auto" px={{ base: 3, lg: 4 }} color="muted">
              (0)
            </InputLeftElement>

            <Input
              data-selector={`${id}-number-input-field`}
              id={`${id}-number-input-field`}
              name={phoneNumberName}
              onChange={handleNumberChange}
              type="text"
              inputMode="tel"
              autoComplete="tel-national"
              value={numberValue}
              isInvalid={isNumberInputInvalid}
              onBlur={onBlur}
              onFocus={handleFocus}
              size={size}
              pl={{ base: 10, lg: 12 }}
              pr={{ base: 1, lg: 4 }}
              disabled={isDisabled}
              {...inputProps}
            />
          </InputGroup>

          <FormLabel
            data-selector={`${id}-number-label`}
            htmlFor={`${id}-number-input-field`}
            id={`${id}-number-input-label`}
            fontSize="sm"
            fontWeight="medium"
          >
            {phoneLabel}
          </FormLabel>
        </Flex>

        {canUndo && <UndoEdit id={id} onUndo={onUndo} />}
      </Flex>
    </CustomFormControl>
  );
};

export type { IPhoneNumberInputV2Props as IPhoneNumberInputBaseProps }; // temporary alias if needed

// Re-export RHF wrapper
export { PhoneNumberInputV2WithRHF } from './PhoneNumberInputV2WithRHF';
